import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Home, Boxes, SlidersHorizontal, User, QrCode } from "lucide-react";
import { QRScannerModal } from "./QRScannerModal";
import { useFilters } from "../lib/FilterContext";

const NavItem = ({ label, icon: Icon, active, onClick, testid, badge }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="relative flex flex-col items-center justify-center gap-1 flex-1 py-2 transition-colors"
  >
    <div className="relative">
      <Icon
        className={`w-5 h-5 ${active ? "text-[#1F5566]" : "text-[#8A93A6]"}`}
        strokeWidth={active ? 2.4 : 2}
      />
      {badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-[#E07A5F] text-white text-[10px] font-bold flex items-center justify-center"
        >
          {badge}
        </span>
      )}
    </div>
    <span className={`text-[10px] font-semibold tracking-wide ${active ? "text-[#1F5566]" : "text-[#8A93A6]"}`}>
      {label}
    </span>
  </button>
);

export const BottomNav = () => {
  const loc = useLocation();
  const navigate = useNavigate();
  const [scanOpen, setScanOpen] = useState(false);
  const { activeCount } = useFilters();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const handler = () => setScanOpen(true);
    window.addEventListener("qfresh:open-scanner", handler);
    return () => window.removeEventListener("qfresh:open-scanner", handler);
  }, []);

  // Auto-open the scanner when navigated to with ?scan=1 (used by the
  // "Log Another Container" button on the confirmation page).
  useEffect(() => {
    if (searchParams.get("scan") === "1") {
      setScanOpen(true);
      // Clean the param so refreshing doesn't reopen the modal.
      const next = new URLSearchParams(searchParams);
      next.delete("scan");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const isActive = (p) => loc.pathname === p;

  return (
    <>
      <div
        data-testid="bottom-nav"
        className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] z-40 pb-[env(safe-area-inset-bottom)]"
      >
        <div className="relative bg-white/95 backdrop-blur-xl border-t border-[#E2E0D8] shadow-[0_-4px_24px_rgba(45,49,66,0.06)]">
          <div className="flex items-end h-16">
            <NavItem
              testid="nav-home"
              label="Home"
              icon={Home}
              active={isActive("/")}
              onClick={() => navigate("/")}
            />
            <NavItem
              testid="nav-inventory"
              label="Inventory"
              icon={Boxes}
              active={isActive("/inventory")}
              onClick={() => navigate("/inventory")}
            />
            <div className="w-16 shrink-0" aria-hidden />
            <NavItem
              testid="nav-filters"
              label="Filters"
              icon={SlidersHorizontal}
              active={isActive("/filters")}
              onClick={() => navigate("/filters")}
              badge={activeCount}
            />
            <NavItem
              testid="nav-profile"
              label="Profile"
              icon={User}
              active={isActive("/profile")}
              onClick={() => navigate("/profile")}
            />
          </div>

          <button
            onClick={() => setScanOpen(true)}
            data-testid="nav-scan-btn"
            className="absolute left-1/2 -translate-x-1/2 -top-7 w-16 h-16 rounded-full brand-gradient-bg text-white flex items-center justify-center shadow-[0_8px_24px_rgba(31,85,102,0.45)] ring-4 ring-[#FDFBF7] transition-transform active:scale-95"
            aria-label="Scan QR"
          >
            <QrCode className="w-7 h-7" strokeWidth={2.4} />
          </button>
        </div>
      </div>

      <div className="h-20" aria-hidden />

      <QRScannerModal open={scanOpen} onClose={() => setScanOpen(false)} />
    </>
  );
};
